<template>
  <HrPageLayout
    title="Confirmation & probation"
    subtitle="Pulled from Employee Register (employment type: Probation). Confirm or extend probation."
    icon="✅"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Public Service Board' },
      { label: 'Confirmation & probation' }
    ]"
  >
    <div class="table-card">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 class="mb-0">Probation employees</h6>
        <button type="button" class="hr-btn hr-btn-outline-primary hr-btn-sm" @click="loadData">Refresh</button>
      </div>

      <div v-if="alerts.length" class="alert alert-warning py-2">
        <strong>Alert:</strong> {{ alerts.length }} employee(s) have one month or less remaining before probation ends.
      </div>

      <div class="table-responsive">
        <table class="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Start</th>
              <th>End</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Remaining</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading"><td colspan="8" class="text-muted">Loading...</td></tr>
            <tr v-else-if="!rows.length"><td colspan="8" class="text-muted">No employees on probation.</td></tr>
            <tr v-for="r in rows" :key="'pb-' + r.employee_id">
              <td>
                {{ r.employee_name }}<br>
                <small class="text-muted">{{ r.empNo || "—" }}</small>
              </td>
              <td>{{ r.departmentName || "—" }}</td>
              <td>{{ r.probation_start || "—" }}</td>
              <td>{{ r.probation_end || "—" }}</td>
              <td>{{ r.status }}</td>
              <td style="min-width: 180px;">
                <div class="progress" style="height: 14px;">
                  <div class="progress-bar" role="progressbar" :style="{ width: `${Math.max(0, Math.min(100, Number(r.progress_percent || 0)))}%` }">
                    {{ Math.max(0, Math.min(100, Number(r.progress_percent || 0))) }}%
                  </div>
                </div>
              </td>
              <td>
                <span :class="r.alert_one_month ? 'text-danger fw-bold' : ''">
                  {{ remainingLabel(r.remaining_days) }}
                </span>
              </td>
              <td>
                <button
                  v-if="canManage && r.status !== 'Confirmed'"
                  type="button"
                  class="hr-btn hr-btn-sm hr-btn-success"
                  @click="confirmRow(r)"
                >
                  Confirm
                </button>
                <button
                  v-if="canManage && r.status !== 'Confirmed'"
                  type="button"
                  class="hr-btn hr-btn-sm hr-btn-outline-primary ms-1"
                  @click="openExtend(r)"
                >
                  Extend Probation
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="showExtendModal" class="modal-overlay" @click.self="showExtendModal = false">
      <div class="modal-card">
        <h5 class="mb-3">Extend probation</h5>
        <div class="form-group">
          <label>Employee</label>
          <input type="text" class="form-control" :value="selectedRow ? selectedRow.employee_name : ''" disabled>
        </div>
        <div class="form-group">
          <label>Extend by (months)</label>
          <input v-model.number="extendMonths" type="number" min="1" max="24" class="form-control">
        </div>
        <div class="form-group">
          <label>Notes</label>
          <textarea v-model="extendNotes" rows="3" class="form-control"></textarea>
        </div>
        <div class="modal-actions">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showExtendModal = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="submitExtend">
            {{ saving ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute, getRight, toast } from "@/api";

export default {
  name: "ModuleConfirmationProbation",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      alerts: [],
      canManage: false,
      showExtendModal: false,
      selectedRow: null,
      extendMonths: 1,
      extendNotes: ""
    };
  },
  mounted() {
    this.canManage = !!getRight("CONFIRMATION_PROBATION_MANAGE");
    this.loadData();
  },
  methods: {
    remainingLabel(days) {
      const d = Number(days || 0);
      if (d < 0) return `${Math.abs(d)} day(s) overdue`;
      return `${d} day(s) left`;
    },
    async loadData() {
      this.loading = true;
      try {
        const fd = new FormData();
        fd.append("function", "getProbationEmployeesDashboard");
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          this.rows = res?.data?.data?.data || [];
          this.alerts = res?.data?.data?.alerts || [];
          this.canManage = !!(res?.data?.data?.permissions?.manage || this.canManage);
        } else {
          this.rows = [];
          this.alerts = [];
          toast.error(res?.data?.message || "Failed to load probation data");
        }
      } catch (e) {
        this.rows = [];
        this.alerts = [];
        toast.error("Failed to load probation data");
      } finally {
        this.loading = false;
      }
    },
    async confirmRow(r) {
      if (!window.confirm(`Confirm probation for ${r.employee_name}?`)) return;
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "confirmProbationEmployee");
        fd.append("employee_id", String(r.employee_id));
        fd.append("notes", "Confirmed via Confirmation & Probation module");
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Employee confirmed");
          await this.loadData();
        } else {
          toast.error(res?.data?.message || "Confirmation failed");
        }
      } catch (_) {
        toast.error("Confirmation failed");
      } finally {
        this.saving = false;
      }
    },
    openExtend(r) {
      this.selectedRow = r;
      this.extendMonths = 1;
      this.extendNotes = "";
      this.showExtendModal = true;
    },
    async submitExtend() {
      if (!this.selectedRow) return;
      if (!this.extendMonths || Number(this.extendMonths) < 1) {
        toast.error("Enter extension months");
        return;
      }
      this.saving = true;
      try {
        const fd = new FormData();
        fd.append("function", "extendProbationEmployee");
        fd.append("employee_id", String(this.selectedRow.employee_id));
        fd.append("extend_months", String(Number(this.extendMonths)));
        fd.append("notes", this.extendNotes || "");
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success("Probation extended");
          this.showExtendModal = false;
          await this.loadData();
        } else {
          toast.error(res?.data?.message || "Extension failed");
        }
      } catch (_) {
        toast.error("Extension failed");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.table-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 8px; padding: 1rem; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-card { background: #fff; border-radius: 8px; padding: 1rem; width: 92%; max-width: 520px; }
.form-group { margin-bottom: 0.75rem; }
.modal-actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
</style>
